import { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
  Mail,
  Phone,
  Send,
  CalendarClock,
  Clock,
  Sparkles,
  Home,
  MapPin,
  User,
  MessageSquare,
  CheckCircle2,
  ChevronDown,
  TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import { submitContact } from "@/lib/api";
import { CONTACT } from "@/lib/data";

// --- Constants ---
const SITUATION_OPTIONS = [
  { value: "jamais", label: "Pas encore loué" },
  { value: "longue_duree", label: "Loué en longue durée" },
  { value: "airbnb_perso", label: "Géré moi-même sur Airbnb" },
  { value: "autre_concierge", label: "Avec une autre conciergerie" },
  { value: "projet_achat", label: "Projet ou en cours d'achat" },
];

const USAGE_OPTIONS = [
  { value: "residence_secondaire", label: "Résidence secondaire" },
  { value: "investissement", label: "Investissement locatif" },
  { value: "residence_principale", label: "Résidence principale (locations courtes)" },
  { value: "autre", label: "Autre" },
];

const DELAI_OPTIONS = [
  { value: "asap", label: "Dès que possible" },
  { value: "1_3_mois", label: "Dans 1 à 3 mois" },
  { value: "3_6_mois", label: "Dans 3 à 6 mois" },
  { value: "plus_tard", label: "Plus tard / Je me renseigne" },
];

const DISPO_OPTIONS = [
  { value: "matin", label: "Matin (10h-12h)" },
  { value: "apres_midi", label: "Après-midi (14h-17h)" },
  { value: "indifferent", label: "Indifférent" },
];

const AMENITY_LABELS = {
  vue_mer: "Vue mer",
  piscine: "Piscine",
  jacuzzi: "Jacuzzi",
  jardin: "Jardin",
  terrasse: "Terrasse",
  balcon: "Balcon",
  parking: "Parking",
  garage: "Garage",
  cheminee: "Cheminée",
  climatisation: "Climatisation",
  wifi: "Wi-Fi",
  lave_vaisselle: "Lave-vaisselle",
  lave_linge: "Lave-linge",
  tv: "TV",
  velos: "Vélos",
  animaux_acceptes: "Animaux acceptés",
};

const initialState = {
  nom: "",
  prenom: "",
  telephone: "",
  email: "",
  ville: "",
  code_postal: "",
  adresse: "",
  type_logement: "",
  nb_chambres: "",
  nb_sdb: "",
  capacite: "",
  surface_m2: "",
  equipements: [],
  situation_actuelle: "",
  usage_bien: "",
  delai_demarrage: "",
  dispo_appel: "indifferent",
  message: "",
  rgpd_consent: false,
};

const formatEUR = (n) =>
  new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(n || 0);

const ContactForm = () => {
  const location = useLocation();
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [simRecap, setSimRecap] = useState(null);

  // Pre-fill from simulator (when navigated with state.simResult / simForm)
  // OR from a city CTA (state.prefill_ville → "Dinard", "Saint-Malo", etc.)
  useEffect(() => {
    const s = location.state;
    if (!s?.simResult && !s?.simForm && !s?.prefill_ville) return;
    const sim = s.simResult;
    const sf = s.simForm;
    setForm((f) => ({
      ...f,
      ville: sim?.city || sf?.custom_city || s?.prefill_ville || f.ville,
      code_postal: sf?.postal_code || f.code_postal,
      type_logement: sf?.property_type || f.type_logement,
      nb_chambres: sf?.bedrooms ?? f.nb_chambres,
      nb_sdb: sf?.bathrooms ?? f.nb_sdb,
      capacite: sf?.capacity ?? f.capacite,
      equipements: sf?.amenities || f.equipements,
    }));
    if (sim) setSimRecap(sim);
    // Smooth scroll to form
    setTimeout(() => {
      const el = document.getElementById("contact-form-section");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 250);
  }, [location.state]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // ----- Completion score -----
  // Each section yields a sub-score; total weighted to give realistic progress.
  const completion = useMemo(() => {
    const has = (v) => v !== "" && v !== null && v !== undefined && v !== false;
    const hasNum = (v) => v !== "" && v !== null && v !== undefined && Number(v) > 0;

    // Section A, Logement (max 6 points)
    const sectionA = [
      has(form.code_postal) || has(form.ville),
      has(form.type_logement),
      hasNum(form.nb_chambres),
      hasNum(form.capacite),
      hasNum(form.nb_sdb) || hasNum(form.surface_m2) || has(form.adresse),
      form.equipements.length >= 2,
    ].filter(Boolean).length;

    // Section B, Situation (max 3)
    const sectionB = [
      has(form.situation_actuelle),
      has(form.usage_bien),
      has(form.delai_demarrage),
    ].filter(Boolean).length;

    // Section C, Coordonnées (max 4, all required)
    const sectionC = [
      has(form.prenom),
      has(form.nom),
      has(form.telephone),
      has(form.email),
    ].filter(Boolean).length;

    // Section D, Précisions (max 1 bonus)
    const sectionD = (form.message?.length || 0) >= 15 ? 1 : 0;

    const ratios = {
      A: sectionA / 6,
      B: sectionB / 3,
      C: sectionC / 4,
      D: sectionD / 1,
    };

    // Weighted: contact 35%, logement 30%, situation 25%, message 10%
    const percent = Math.round(
      (ratios.C * 0.35 + ratios.A * 0.30 + ratios.B * 0.25 + ratios.D * 0.10) *
        100
    );

    const sectionsComplete = [
      sectionA >= 4,
      sectionB === 3,
      sectionC === 4,
      sectionD === 1,
    ].filter(Boolean).length;

    return { percent, sectionsComplete, sectionA, sectionB, sectionC, sectionD };
  }, [form]);

  const toggleEquipement = (key) =>
    setForm((f) => ({
      ...f,
      equipements: f.equipements.includes(key)
        ? f.equipements.filter((a) => a !== key)
        : [...f.equipements, key],
    }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.nom || !form.prenom || !form.telephone || !form.email) {
      toast.error("Merci de remplir vos coordonnées (prénom, nom, téléphone, email).");
      return;
    }
    if (!form.rgpd_consent) {
      toast.error("Merci d'accepter le traitement de vos données pour pouvoir vous recontacter.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        nb_chambres: form.nb_chambres === "" ? null : Number(form.nb_chambres),
        nb_sdb: form.nb_sdb === "" ? null : Number(form.nb_sdb),
        capacite: form.capacite === "" ? null : Number(form.capacite),
        surface_m2: form.surface_m2 === "" ? null : Number(form.surface_m2),
        simulation: simRecap || null,
        source_page: simRecap ? "simulator" : "contact",
      };
      await submitContact(payload);
      setSuccess(true);
      toast.success("Votre demande a bien été envoyée. Nous vous répondrons sous 24h.");
      setForm(initialState);
      setSimRecap(null);
      setTimeout(() => setSuccess(false), 12000);
    } catch (err) {
      toast.error("Une erreur est survenue. Merci de réessayer ou de nous appeler.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact-form"
      className="py-24 lg:py-32 bg-white"
      data-testid="contact-form-section"
    >
      <div className="container-tm grid lg:grid-cols-12 gap-12 lg:gap-16">
        {/* Left column */}
        <aside className="lg:col-span-5">
          <span className="overline">Contact</span>
          <h2 className="mt-5 font-serif font-light text-ink text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
            {simRecap ? (
              <>
                Demandez votre
                <span className="italic text-gold"> étude</span>.
              </>
            ) : (
              <>
                Besoin de plus
                <span className="italic text-gold"> d'informations</span>&thinsp;?
              </>
            )}
          </h2>
          <p className="mt-6 font-sans text-base md:text-lg text-ink/70 leading-relaxed">
            {simRecap
              ? "Nous étudions votre logement en détail et vous proposons une stratégie sur mesure : tarifs saisonniers, photos, plan de diffusion, services à la carte."
              : "Vous souhaitez connaître le potentiel locatif de votre logement ? Contactez-nous pour une étude personnalisée gratuite."}
          </p>

          <div className="mt-10 space-y-4">
            <a
              href={`mailto:${CONTACT.email}`}
              className="flex items-center gap-4 group p-4 rounded-2xl border border-ink/10 hover:border-gold hover:bg-cream/40 transition-all"
              data-testid="contact-email-link"
            >
              <span className="w-12 h-12 rounded-xl bg-gold/10 flex items-center justify-center group-hover:bg-gold transition-colors">
                <Mail size={18} strokeWidth={1.8} className="text-gold group-hover:text-white transition-colors" />
              </span>
              <span>
                <span className="block text-[10px] uppercase tracking-[0.25em] text-sage font-semibold">Email</span>
                <span className="block font-sans text-sm md:text-base text-ink break-all">{CONTACT.email}</span>
              </span>
            </a>

            <a
              href={CONTACT.phoneHref}
              className="flex items-center gap-4 group p-4 rounded-2xl border border-ink/10 hover:border-gold hover:bg-cream/40 transition-all"
              data-testid="contact-phone-link"
            >
              <span className="w-12 h-12 rounded-xl bg-gold/10 flex items-center justify-center group-hover:bg-gold transition-colors">
                <Phone size={18} strokeWidth={1.8} className="text-gold group-hover:text-white transition-colors" />
              </span>
              <span>
                <span className="block text-[10px] uppercase tracking-[0.25em] text-sage font-semibold">Téléphone</span>
                <span className="block font-sans text-base text-ink">{CONTACT.phone}</span>
              </span>
            </a>

            <a
              href={CONTACT.calendly}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 group p-4 rounded-2xl bg-gold text-white hover:bg-gold-500 transition-all shadow-glow-amber"
              data-testid="contact-calendly-link"
            >
              <span className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center">
                <CalendarClock size={18} strokeWidth={1.8} className="text-white" />
              </span>
              <span>
                <span className="block text-[10px] uppercase tracking-[0.25em] text-white/80 font-semibold">Réservation en ligne</span>
                <span className="block font-sans text-base text-white font-semibold">Prendre RDV 15 min →</span>
              </span>
            </a>
          </div>

          <div className="mt-8 p-5 rounded-2xl bg-cream/40 border border-ink/10" data-testid="contact-hours">
            <div className="flex items-center gap-2 mb-3">
              <Clock size={14} strokeWidth={2} className="text-sage" />
              <p className="text-[10px] uppercase tracking-[0.25em] font-semibold text-sage">
                Service commercial
              </p>
            </div>
            <ul className="space-y-2 font-sans text-sm text-ink/75">
              {CONTACT.hours.map((h) => (
                <li key={h.day} className="flex justify-between gap-4">
                  <span className="font-medium text-ink">{h.day}</span>
                  <span className="text-ink/70">{h.slots}</span>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        {/* Right column, form */}
        <div className="lg:col-span-7">
          {success ? (
            <SuccessCard onReset={() => setSuccess(false)} />
          ) : (
            <form
              onSubmit={onSubmit}
              className="bg-cream/40 rounded-3xl p-7 md:p-10 border border-ink/5 shadow-soft space-y-10"
              data-testid="contact-form"
            >
              {/* Sim recap card */}
              {simRecap && <SimRecap recap={simRecap} />}

              {/* Progress tracker */}
              <ProgressTracker completion={completion} hasSim={!!simRecap} />

              {/* Section A, Bien */}
              <Section
                icon={Home}
                title="Votre logement"
                hint={
                  simRecap
                    ? "Pré-rempli depuis votre simulation. Modifiez si besoin."
                    : "Décrivez votre bien pour une étude précise."
                }
              >
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field
                    label="Code postal"
                    name="code_postal"
                    value={form.code_postal}
                    onChange={(v) => update("code_postal", v)}
                    inputMode="numeric"
                    maxLength={5}
                    placeholder="35400"
                    testId="cf-postal"
                  />
                  <Field
                    label="Ville"
                    name="ville"
                    value={form.ville}
                    onChange={(v) => update("ville", v)}
                    placeholder="Saint-Malo"
                    testId="cf-ville"
                  />
                  <div className="sm:col-span-2">
                    <Field
                      label="Adresse (facultatif)"
                      name="adresse"
                      value={form.adresse}
                      onChange={(v) => update("adresse", v)}
                      placeholder="Rue, voie, lieu-dit…"
                      testId="cf-adresse"
                    />
                  </div>

                  <Select
                    label="Type de logement"
                    name="type_logement"
                    value={form.type_logement}
                    onChange={(v) => update("type_logement", v)}
                    testId="cf-type"
                    options={[
                      { value: "", label: "Choisir…" },
                      { value: "Studio", label: "Studio" },
                      { value: "Appartement", label: "Appartement" },
                      { value: "Maison", label: "Maison" },
                      { value: "Villa", label: "Villa" },
                      { value: "Loft", label: "Loft" },
                      { value: "Autre", label: "Autre" },
                    ]}
                  />
                  <Field
                    label="Surface (m², facultatif)"
                    name="surface_m2"
                    value={form.surface_m2}
                    onChange={(v) => update("surface_m2", v.replace(/\D/g, ""))}
                    placeholder="80"
                    inputMode="numeric"
                    testId="cf-surface"
                  />
                  <Field
                    label="Chambres"
                    name="nb_chambres"
                    value={form.nb_chambres}
                    onChange={(v) => update("nb_chambres", v.replace(/\D/g, ""))}
                    inputMode="numeric"
                    testId="cf-chambres"
                  />
                  <Field
                    label="Salles de bain"
                    name="nb_sdb"
                    value={form.nb_sdb}
                    onChange={(v) => update("nb_sdb", v.replace(/\D/g, ""))}
                    inputMode="numeric"
                    testId="cf-sdb"
                  />
                  <div className="sm:col-span-2">
                    <Field
                      label="Capacité d'accueil (personnes)"
                      name="capacite"
                      value={form.capacite}
                      onChange={(v) => update("capacite", v.replace(/\D/g, ""))}
                      inputMode="numeric"
                      placeholder="4"
                      testId="cf-capacite"
                    />
                  </div>
                </div>

                {/* Equipements */}
                <div className="mt-7">
                  <Label>Équipements</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {Object.entries(AMENITY_LABELS).map(([key, label]) => {
                      const active = form.equipements.includes(key);
                      return (
                        <button
                          key={key}
                          type="button"
                          data-testid={`cf-equip-${key}`}
                          onClick={() => toggleEquipement(key)}
                          className={`text-xs sm:text-sm px-3 py-2.5 rounded-xl border transition-all text-left flex items-center gap-2 ${
                            active
                              ? "border-gold bg-gold/10 text-ink font-medium"
                              : "border-ink/12 bg-white text-ink/70 hover:border-gold/40"
                          }`}
                        >
                          {active && (
                            <CheckCircle2
                              size={13}
                              strokeWidth={2.4}
                              className="text-gold shrink-0"
                            />
                          )}
                          <span>{label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </Section>

              {/* Section B, Situation */}
              <Section
                icon={MapPin}
                title="Votre situation"
                hint="Aidez-nous à mieux comprendre votre projet."
              >
                <RadioGroup
                  label="Situation actuelle du bien"
                  name="situation_actuelle"
                  value={form.situation_actuelle}
                  onChange={(v) => update("situation_actuelle", v)}
                  options={SITUATION_OPTIONS}
                  testId="cf-situation"
                />
                <RadioGroup
                  label="Type d'usage"
                  name="usage_bien"
                  value={form.usage_bien}
                  onChange={(v) => update("usage_bien", v)}
                  options={USAGE_OPTIONS}
                  testId="cf-usage"
                />
                <RadioGroup
                  label="Quand souhaitez-vous démarrer ?"
                  name="delai_demarrage"
                  value={form.delai_demarrage}
                  onChange={(v) => update("delai_demarrage", v)}
                  options={DELAI_OPTIONS}
                  testId="cf-delai"
                />
              </Section>

              {/* Section C, Coordonnées */}
              <Section
                icon={User}
                title="Vos coordonnées"
                hint="Pour vous recontacter dans les 24h."
              >
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field
                    label="Prénom *"
                    name="prenom"
                    value={form.prenom}
                    onChange={(v) => update("prenom", v)}
                    testId="input-prenom"
                  />
                  <Field
                    label="Nom *"
                    name="nom"
                    value={form.nom}
                    onChange={(v) => update("nom", v)}
                    testId="input-nom"
                  />
                  <Field
                    label="Téléphone *"
                    name="telephone"
                    type="tel"
                    value={form.telephone}
                    onChange={(v) => update("telephone", v)}
                    testId="input-telephone"
                  />
                  <Field
                    label="Email *"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={(v) => update("email", v)}
                    testId="input-email"
                  />
                </div>
                <div className="mt-5">
                  <RadioGroup
                    label="Disponibilité pour un appel"
                    name="dispo_appel"
                    value={form.dispo_appel}
                    onChange={(v) => update("dispo_appel", v)}
                    options={DISPO_OPTIONS}
                    testId="cf-dispo"
                    inline
                  />
                </div>
              </Section>

              {/* Section D, Message */}
              <Section
                icon={MessageSquare}
                title="Précisions (facultatif)"
              >
                <textarea
                  name="message"
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  rows={5}
                  data-testid="input-message"
                  placeholder="Particularités du logement, questions, contraintes spécifiques…"
                  className="w-full bg-white border border-ink/10 rounded-xl px-4 py-3 font-sans text-sm text-ink focus:outline-none focus:ring-2 focus:ring-gold/40 focus:border-gold transition-all resize-none"
                />
              </Section>

              {/* RGPD */}
              <label
                className="flex items-start gap-3 cursor-pointer group"
                data-testid="cf-rgpd-label"
              >
                <input
                  type="checkbox"
                  checked={form.rgpd_consent}
                  onChange={(e) => update("rgpd_consent", e.target.checked)}
                  data-testid="cf-rgpd"
                  className="mt-1 w-4 h-4 accent-gold cursor-pointer"
                />
                <span className="text-xs text-ink/70 font-sans leading-relaxed">
                  J'accepte que TERRE ET MER CONCIERGERIE traite mes données pour me
                  recontacter au sujet de mon projet (conformément à notre{" "}
                  <a
                    href="/mentions-legales"
                    className="underline decoration-gold underline-offset-2 hover:text-gold transition-colors"
                  >
                    politique RGPD
                  </a>
                  ). Vos données ne seront jamais cédées.
                </span>
              </label>

              {/* Submit */}
              <div className="border-t border-ink/10 pt-7">
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-form-submit"
                  className="btn-accent w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed group"
                >
                  {loading ? (
                    "Envoi en cours…"
                  ) : (
                    <>
                      Envoyer ma demande
                      <Send
                        size={14}
                        strokeWidth={2}
                        className="ml-3 group-hover:translate-x-1 transition-transform"
                      />
                    </>
                  )}
                </button>

                <p className="mt-5 text-xs text-ink/50 font-sans leading-relaxed">
                  * Champs obligatoires. Vos données restent strictement confidentielles.
                  Vous pouvez à tout moment vous inscrire gratuitement sur la liste
                  d'opposition au démarchage téléphonique{" "}
                  <a
                    href="https://www.bloctel.gouv.fr"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline decoration-gold underline-offset-2 hover:text-gold transition-colors"
                  >
                    BLOCTEL
                  </a>
                  .
                </p>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

// ===== Subcomponents =====
const ProgressTracker = ({ completion, hasSim }) => {
  const { percent, sectionsComplete } = completion;
  const tier =
    percent >= 90
      ? "excellent"
      : percent >= 65
      ? "good"
      : percent >= 35
      ? "ok"
      : "start";

  const tierConfig = {
    start: {
      label: "Commençons",
      message: hasSim
        ? "Votre simulation est pré-remplie. Complétez vos coordonnées pour recevoir l'étude."
        : "Plus le formulaire est complet, plus notre étude sera précise.",
      color: "text-ink/60",
    },
    ok: {
      label: "Bon début",
      message: "Encore quelques détails pour un dossier solide.",
      color: "text-amber-600",
    },
    good: {
      label: "Presque parfait",
      message: "Votre dossier est bien complet, encore un effort !",
      color: "text-amber-700",
    },
    excellent: {
      label: "Dossier complet",
      message: "Excellent ! Notre équipe pourra étudier votre projet en profondeur.",
      color: "text-emerald-700",
    },
  };
  const cfg = tierConfig[tier];

  return (
    <div
      data-testid="cf-progress"
      className="rounded-2xl bg-white border border-ink/8 p-5 md:p-6 shadow-sm"
    >
      <div className="flex items-center justify-between gap-4 mb-3">
        <div className="flex items-center gap-2.5">
          <div
            className={`w-9 h-9 rounded-xl flex items-center justify-center transition-colors ${
              tier === "excellent"
                ? "bg-emerald-100"
                : tier === "good"
                ? "bg-gold/15"
                : tier === "ok"
                ? "bg-amber-100"
                : "bg-ink/5"
            }`}
          >
            <TrendingUp
              size={16}
              strokeWidth={1.8}
              className={
                tier === "excellent"
                  ? "text-emerald-700"
                  : tier === "good"
                  ? "text-gold"
                  : tier === "ok"
                  ? "text-amber-700"
                  : "text-ink/45"
              }
            />
          </div>
          <div>
            <p className="font-serif text-base text-ink leading-tight">
              Qualité du dossier
            </p>
            <p
              className={`text-xs font-sans mt-0.5 ${cfg.color}`}
              data-testid="cf-progress-tier"
            >
              {cfg.label}
            </p>
          </div>
        </div>
        <div className="text-right">
          <p
            className="font-serif text-2xl text-ink leading-none tabular-nums"
            data-testid="cf-progress-percent"
          >
            {percent}
            <span className="text-base text-ink/55">%</span>
          </p>
          <p
            className="text-[10px] uppercase tracking-[0.18em] font-semibold text-ink/45 mt-1"
            data-testid="cf-progress-sections"
          >
            {sectionsComplete}/4 sections
          </p>
        </div>
      </div>

      {/* Bar */}
      <div className="h-2 bg-ink/8 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ease-out ${
            tier === "excellent"
              ? "bg-gradient-to-r from-emerald-400 to-emerald-600"
              : "bg-gradient-to-r from-gold via-gold to-amber-500"
          }`}
          style={{ width: `${Math.max(percent, 4)}%` }}
        />
      </div>

      {/* Helper message */}
      <p className="mt-3 text-xs text-ink/65 font-sans leading-relaxed">
        {cfg.message}
      </p>

      {/* Section dots */}
      <div className="mt-4 grid grid-cols-4 gap-2">
        <Dot label="Bien" active={completion.sectionA >= 4} />
        <Dot label="Situation" active={completion.sectionB === 3} />
        <Dot label="Contact" active={completion.sectionC === 4} />
        <Dot label="Précisions" active={completion.sectionD === 1} />
      </div>
    </div>
  );
};

const Dot = ({ label, active }) => (
  <div className="flex items-center gap-2">
    <span
      className={`w-2 h-2 rounded-full shrink-0 transition-colors ${
        active ? "bg-gold" : "bg-ink/15"
      }`}
    />
    <span
      className={`text-[10px] uppercase tracking-[0.16em] font-semibold transition-colors ${
        active ? "text-ink" : "text-ink/40"
      }`}
    >
      {label}
    </span>
  </div>
);

const Section = ({ icon: Icon, title, hint, children }) => (
  <div data-testid={`section-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
    <div className="flex items-start gap-3 mb-5">
      <div className="w-9 h-9 rounded-xl bg-gold/10 flex items-center justify-center shrink-0">
        <Icon size={16} strokeWidth={1.8} className="text-gold" />
      </div>
      <div>
        <p className="font-serif text-xl text-ink leading-tight">{title}</p>
        {hint && <p className="mt-1 text-xs text-ink/55 font-sans">{hint}</p>}
      </div>
    </div>
    <div>{children}</div>
  </div>
);

const Label = ({ children }) => (
  <label className="block text-[10px] uppercase tracking-[0.22em] font-semibold text-sage mb-3">
    {children}
  </label>
);

const Field = ({ label, name, type = "text", value, onChange, placeholder, testId, inputMode, maxLength }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-[0.22em] font-semibold text-sage mb-2">
      {label}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      inputMode={inputMode}
      maxLength={maxLength}
      data-testid={testId}
      className="w-full bg-white border border-ink/10 rounded-xl px-4 py-3 font-sans text-sm text-ink focus:outline-none focus:ring-2 focus:ring-gold/40 focus:border-gold transition-all"
    />
  </div>
);

const Select = ({ label, name, value, onChange, options, testId }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-[0.22em] font-semibold text-sage mb-2">
      {label}
    </label>
    <div className="relative">
      <select
        name={name}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full appearance-none bg-white border border-ink/10 rounded-xl px-4 py-3 pr-10 font-sans text-sm text-ink focus:outline-none focus:ring-2 focus:ring-gold/40 focus:border-gold transition-all"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <ChevronDown
        size={16}
        strokeWidth={2}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-ink/40 pointer-events-none"
      />
    </div>
  </div>
);

const RadioGroup = ({ label, name, value, onChange, options, testId, inline }) => (
  <div className="mb-5 last:mb-0">
    <Label>{label}</Label>
    <div
      className={
        inline
          ? "flex flex-wrap gap-2"
          : "grid sm:grid-cols-2 gap-2"
      }
    >
      {options.map((o) => {
        const active = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            data-testid={`${testId}-${o.value}`}
            onClick={() => onChange(o.value)}
            className={`text-left text-sm font-sans px-4 py-2.5 rounded-xl border transition-all flex items-center gap-2 ${
              active
                ? "border-gold bg-gold/10 text-ink font-medium"
                : "border-ink/12 bg-white text-ink/70 hover:border-gold/40"
            }`}
          >
            <span
              className={`w-3.5 h-3.5 rounded-full border-2 transition-all shrink-0 ${
                active ? "border-gold bg-gold" : "border-ink/25 bg-white"
              }`}
            >
              {active && (
                <span className="block w-full h-full rounded-full bg-white scale-[0.4]" />
              )}
            </span>
            <span>{o.label}</span>
          </button>
        );
      })}
    </div>
  </div>
);

const SimRecap = ({ recap }) => {
  const monthlyNet = Math.round(recap.monthly_gross_avg * 0.85);
  return (
    <div
      className="rounded-2xl bg-gradient-to-br from-ink to-[#0d1a26] text-white p-6 md:p-7 grain-overlay relative overflow-hidden"
      data-testid="cf-sim-recap"
    >
      <div className="absolute -top-20 -right-20 w-[260px] h-[260px] bg-gold/20 rounded-full blur-[100px] pointer-events-none" />
      <div className="relative">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={13} strokeWidth={2} className="text-gold" />
          <span className="overline text-gold">Votre simulation</span>
        </div>
        <p className="font-serif text-lg md:text-xl text-white/95 leading-snug">
          {recap.city}, {formatEUR(recap.nightly_price_avg)}/nuit en moyenne,
          soit environ <strong className="text-gold">{formatEUR(monthlyNet)}</strong> de
          revenu net mensuel après commission.
        </p>
        <p className="mt-3 text-xs text-white/55 font-sans">
          Données pré-remplies dans le formulaire ci-dessous.
        </p>
      </div>
    </div>
  );
};

const SuccessCard = ({ onReset }) => (
  <div
    className="bg-gradient-to-br from-cream to-sand rounded-3xl p-10 border border-gold/30 shadow-soft text-center"
    data-testid="contact-success"
  >
    <div className="w-16 h-16 rounded-full bg-gold mx-auto flex items-center justify-center mb-6">
      <CheckCircle2 size={28} strokeWidth={1.8} className="text-white" />
    </div>
    <h3 className="font-serif text-3xl text-ink leading-tight">
      Demande bien <span className="italic text-gold">reçue</span>.
    </h3>
    <p className="mt-4 text-ink/70 font-sans max-w-md mx-auto">
      Notre équipe vous recontacte sous 24h ouvrées pour étudier votre projet.
      Si c'est urgent, vous pouvez aussi nous appeler ou prendre RDV directement.
    </p>
    <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
      <a
        href={CONTACT.calendly}
        target="_blank"
        rel="noopener noreferrer"
        className="btn-accent group"
        data-testid="success-calendly"
      >
        Prendre RDV maintenant
        <CalendarClock size={14} strokeWidth={2} className="ml-2" />
      </a>
      <button
        type="button"
        onClick={onReset}
        data-testid="success-reset"
        className="btn-ghost"
      >
        Envoyer une autre demande
      </button>
    </div>
  </div>
);

export default ContactForm;
